import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import { Users, FileText, TrendingUp, CheckCircle, Clock, Euro, Plus } from 'lucide-react';

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid={`stat-${label.replace(/\s+/g,'-').toLowerCase()}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon size={16} />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetches = [api.get('/articles/all?limit=5')];
    if (['admin', 'editor'].includes(user?.role)) {
      fetches.push(api.get('/admin/stats'));
    }
    Promise.all(fetches).then(([artRes, statsRes]) => {
      setRecentArticles(artRes.data.articles || []);
      if (statsRes) setStats(statsRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const STATUS_LABELS = { draft: 'Rascunho', pending: 'Pendente', published: 'Publicado', rejected: 'Rejeitado' };
  const STATUS_COLORS = { draft: 'badge-gray', pending: 'badge-gold', published: 'badge-green', rejected: 'badge-red' };

  return (
    <div data-testid="dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('backoffice.dashboard')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">Bem-vindo(a), {user?.full_name?.split(' ')[0]}!</p>
        </div>
        <Link to="/admin/artigos/novo" data-testid="new-article-btn"
          className="flex items-center gap-2 px-4 py-2 bg-[#CC0000] text-white text-sm font-medium rounded-md hover:bg-[#A30000] transition-colors">
          <Plus size={14} /> {t('backoffice.new_article')}
        </Link>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label={t('backoffice.total_users')} value={stats.users} color="bg-blue-50 text-blue-600" />
          <StatCard icon={FileText} label={t('backoffice.total_articles')} value={stats.articles}
            sub={`${stats.published_articles} publicados`} color="bg-green-50 text-green-600" />
          <StatCard icon={Clock} label={t('backoffice.pending')} value={stats.pending_articles}
            sub="Aguardam revisão" color="bg-yellow-50 text-yellow-600" />
          <StatCard icon={Euro} label={t('backoffice.revenue')} value={`€${stats.revenue?.toFixed(2) || '0.00'}`}
            sub={`${stats.payments} pagamentos`} color="bg-red-50 text-red-600" />
        </div>
      )}

      {/* Recent Articles */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">{user?.role === 'publisher' ? 'Os Meus Artigos' : 'Artigos Recentes'}</h2>
          <Link to="/admin/artigos" className="text-xs text-[#CC0000] hover:underline">{t('common.view_all')}</Link>
        </div>
        {loading ? (
          <div className="p-5 space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {recentArticles.map(a => (
              <div key={a.id} className="px-5 py-3.5 flex items-center gap-3 hover:bg-gray-50 transition-colors" data-testid={`article-row-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{a.title}</p>
                  <p className="text-xs text-gray-400">{a.author_name} · {new Date(a.created_at).toLocaleDateString('pt')}</p>
                </div>
                <span className={`badge ${STATUS_COLORS[a.status]}`}>{STATUS_LABELS[a.status] || a.status}</span>
                <Link to={`/admin/artigos/${a.id}/editar`} className="text-xs text-gray-400 hover:text-[#CC0000] transition-colors">{t('common.edit')}</Link>
              </div>
            ))}
            {recentArticles.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-gray-400">Nenhum artigo ainda. <Link to="/admin/artigos/novo" className="text-[#CC0000] hover:underline">Criar artigo</Link></div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
